import { useState, useEffect, useRef } from 'react';
import { X, CircleAlert } from 'lucide-react';
import type { Engagement, Entity, JournalEntryHeader } from '../types';
import {
  createJournalEntryHeader,
  updateJournalEntryHeader,
  validateNoDuplicateJENumber,
} from '../db/database';

interface JournalEntryModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (newHeader?: JournalEntryHeader) => void;
  engagement: Engagement;
  entities: Entity[];
  headerToEdit?: JournalEntryHeader | null;
}

interface FormData {
  entityId: string;
  jeNumber: string;
  jeName: string;
  isRJE: boolean;
}

interface ValidationErrors {
  jeNumber?: string;
  jeName?: string;
}

export default function JournalEntryModal({
  isOpen,
  onClose,
  onSave,
  engagement,
  entities,
  headerToEdit,
}: JournalEntryModalProps) {
  const [formData, setFormData] = useState<FormData>({
    entityId: entities[0]?.id ?? '',
    jeNumber: '',
    jeName: '',
    isRJE: false,
  });

  const [errors, setErrors] = useState<ValidationErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [shouldShake, setShouldShake] = useState(false);

  const isEditMode = !!headerToEdit;
  const isMultiEntity = entities.length > 0;
  const jeNumberRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isOpen) {
      setTimeout(() => jeNumberRef.current?.focus(), 0);
      if (headerToEdit) {
        setFormData({
          entityId: headerToEdit.entityId ?? '',
          jeNumber: headerToEdit.jeNumber,
          jeName: headerToEdit.jeName,
          isRJE: headerToEdit.isRJE,
        });
      } else {
        setFormData({ entityId: entities[0]?.id ?? '', jeNumber: '', jeName: '', isRJE: false });
      }
      setErrors({});
      setShouldShake(false);
    }
  }, [isOpen, headerToEdit, entities]);

  const validateForm = async (): Promise<boolean> => {
    const newErrors: ValidationErrors = {};

    if (!formData.jeNumber.trim()) {
      newErrors.jeNumber = 'Journal Entry Number is required';
    } else {
      const isUnique = await validateNoDuplicateJENumber(
        engagement.id!,
        formData.jeNumber.trim(),
        headerToEdit?.id
      );
      if (!isUnique) {
        newErrors.jeNumber = 'This JE Number already exists in this engagement';
      }
    }

    if (!formData.jeName.trim()) {
      newErrors.jeName = 'Journal Entry Name is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    const isValid = await validateForm();

    if (!isValid) {
      setShouldShake(true);
      setTimeout(() => setShouldShake(false), 500);
      return;
    }

    setIsSubmitting(true);

    try {
      if (isEditMode && headerToEdit) {
        await updateJournalEntryHeader({
          ...headerToEdit,
          jeNumber: formData.jeNumber.trim(),
          jeName: formData.jeName.trim(),
          isRJE: formData.isRJE,
        });
        onSave();
      } else {
        const entityId = formData.entityId || undefined;
        const id = await createJournalEntryHeader({
          engagementId: engagement.id!,
          jeNumber: formData.jeNumber.trim(),
          jeName: formData.jeName.trim(),
          isRJE: formData.isRJE,
          totalDebits: 0,
          signoffState: null,
          notes: '',
          calcType: 'Manual',
          calcInputs: {},
          entityId,
        });
        const newHeader: JournalEntryHeader = {
          id,
          engagementId: engagement.id!,
          jeNumber: formData.jeNumber.trim(),
          jeName: formData.jeName.trim(),
          isRJE: formData.isRJE,
          totalDebits: 0,
          signoffState: null,
          notes: '',
          entityId,
        };
        onSave(newHeader);
      }

      onClose();
    } catch (error) {
      console.error('Error saving journal entry:', error);
      alert('An error occurred while saving. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (field: keyof Pick<FormData, 'jeNumber' | 'jeName'>, value: string) => {
    setFormData({ ...formData, [field]: value });
    if (errors[field]) {
      setErrors({ ...errors, [field]: undefined });
    }
  };

  if (!isOpen || !engagement.id) return null;

  // In edit mode, resolve the entity name for display
  const editEntityName = isEditMode && headerToEdit?.entityId
    ? entities.find(e => e.id === headerToEdit.entityId)?.name
    : undefined;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/60 backdrop-blur-sm">
      <div
        className={`glass-panel rounded-lg shadow-xl max-w-md w-full mx-4 transition-colors duration-300 ${
          shouldShake ? 'animate-shake' : ''
        }`}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-slate-200 dark:border-slate-800">
          <div>
            <h2 className="text-xl font-bold text-slate-900 dark:text-slate-100">
              {isEditMode ? 'Edit Journal Entry' : 'New Journal Entry'}
            </h2>
            <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5">
              {engagement.clientName}
            </p>
          </div>
          <button
            onClick={onClose}
            disabled={isSubmitting}
            className="p-1 rounded hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <X className="w-5 h-5 text-taxtrail-red dark:text-red-400" />
          </button>
        </div>

        {/* Form Content */}
        <div className="p-6 space-y-4">
          {/* Entity — shown only in multi-entity mode */}
          {isMultiEntity && (
            <div>
              <label className="block text-sm font-medium mb-1 text-slate-900 dark:text-slate-100">
                Entity<span className="text-taxtrail-red dark:text-red-400">*</span>
              </label>
              {isEditMode ? (
                <div className="px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50 text-slate-600 dark:text-slate-400 text-sm">
                  {editEntityName ?? '—'}
                </div>
              ) : (
                <select
                  value={formData.entityId}
                  onChange={(e) => setFormData({ ...formData, entityId: e.target.value })}
                  disabled={isSubmitting}
                  className="w-full px-3 py-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-sage-500 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 border border-slate-200 dark:border-slate-700 transition-colors duration-200"
                >
                  {entities.map(e => (
                    <option key={e.id} value={e.id}>{e.name}</option>
                  ))}
                </select>
              )}
            </div>
          )}

          {/* JE Number */}
          <div>
            <label className="block text-sm font-medium mb-1 text-slate-900 dark:text-slate-100">
              JE Number<span className="text-taxtrail-red dark:text-red-400">*</span>
            </label>
            <input
              ref={jeNumberRef}
              type="text"
              value={formData.jeNumber}
              onChange={(e) => handleInputChange('jeNumber', e.target.value)}
              disabled={isSubmitting}
              placeholder="e.g. AJE-1"
              className={`w-full px-3 py-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-sage-500 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 transition-colors duration-200 ${
                errors.jeNumber
                  ? 'border border-taxtrail-red dark:border-red-400'
                  : 'border border-slate-200 dark:border-slate-700'
              }`}
            />
            {errors.jeNumber && (
              <p className="text-xs text-taxtrail-red dark:text-red-400 mt-1 flex items-center gap-1">
                <CircleAlert className="w-3 h-3" />
                {errors.jeNumber}
              </p>
            )}
          </div>

          {/* JE Name */}
          <div>
            <label className="block text-sm font-medium mb-1 text-slate-900 dark:text-slate-100">
              JE Name<span className="text-taxtrail-red dark:text-red-400">*</span>
            </label>
            <input
              type="text"
              value={formData.jeName}
              onChange={(e) => handleInputChange('jeName', e.target.value)}
              disabled={isSubmitting}
              placeholder="e.g. To record depreciation"
              className={`w-full px-3 py-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-sage-500 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 transition-colors duration-200 ${
                errors.jeName
                  ? 'border border-taxtrail-red dark:border-red-400'
                  : 'border border-slate-200 dark:border-slate-700'
              }`}
            />
            {errors.jeName && (
              <p className="text-xs text-taxtrail-red dark:text-red-400 mt-1 flex items-center gap-1">
                <CircleAlert className="w-3 h-3" />
                {errors.jeName}
              </p>
            )}
          </div>

          {/* Type Toggle */}
          <div>
            <label className="block text-sm font-medium mb-2 text-slate-900 dark:text-slate-100">
              Type
            </label>
            <div className="inline-flex rounded-lg border border-slate-200 dark:border-slate-700 overflow-hidden">
              <button
                type="button"
                onClick={() => setFormData({ ...formData, isRJE: false })}
                disabled={isSubmitting}
                className={`px-5 py-2 text-sm font-semibold transition-colors duration-150 ${
                  !formData.isRJE
                    ? 'bg-slate-700 text-white dark:bg-slate-200 dark:text-slate-900'
                    : 'bg-white text-slate-500 hover:bg-slate-50 dark:bg-slate-800 dark:text-slate-400 dark:hover:bg-slate-700'
                }`}
              >
                AJE
              </button>
              <button
                type="button"
                onClick={() => setFormData({ ...formData, isRJE: true })}
                disabled={isSubmitting}
                className={`px-5 py-2 text-sm font-semibold transition-colors duration-150 border-l border-slate-200 dark:border-slate-700 ${
                  formData.isRJE
                    ? 'bg-sage-600 text-white'
                    : 'bg-white text-slate-500 hover:bg-slate-50 dark:bg-slate-800 dark:text-slate-400 dark:hover:bg-slate-700'
                }`}
              >
                RJE
              </button>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-slate-200 dark:border-slate-800 flex justify-end">
          <button
            onClick={handleSave}
            disabled={isSubmitting}
            className="px-6 py-2 rounded-lg font-medium bg-sage-600 hover:bg-sage-700 text-white disabled:opacity-50 disabled:cursor-not-allowed transition-colors focus:outline-none focus:ring-2 focus:ring-sage-500 focus:ring-offset-2"
          >
            {isSubmitting ? 'Saving...' : 'SAVE'}
          </button>
        </div>
      </div>

      <style>{`
        @keyframes shake {
          0%, 100% { transform: translateX(0); }
          10%, 30%, 50%, 70%, 90% { transform: translateX(-8px); }
          20%, 40%, 60%, 80% { transform: translateX(8px); }
        }
        .animate-shake {
          animation: shake 0.5s;
        }
      `}</style>
    </div>
  );
}
